import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import type {
  ComposeEmailDelivery,
  ComposeEmailInput,
} from '@realadvisor/common-types';

import { gql } from '@graphql/__generated__';
import { createValueFromString } from '@modules/_shared/components/email-editor/htmlValue';
import {
  hasAgentEmailVariables,
  linkAgentEmailVariables,
} from '@modules/_shared/components/email-form/agentEmailVariables';
import { addCampaignRecipientSuggestions } from '@modules/_shared/components/email-form/campaignSuggestions';
import {
  parseSuggestionsMap,
  toSuggestionLanguage,
} from '@modules/_shared/components/email-form/contextSuggestions';
import { sanitizeAgentEmailHtml } from '@modules/_shared/components/email-form/sanitizeAgentEmailHtml';
import {
  type ComposeEmailFormProps,
  type ComposeEmailParentLink,
  type ComposeEmailTypeLabels,
  composeEmailDraftSubject,
  composeEmailDraftVariables,
  composeEmailFormProps,
  composeEmailSuggestionAnchors,
  parseComposeEmailInput,
  resolveComposeEmailDraft,
} from '@modules/_shared/hooks/composeEmailDraft';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';

export type {
  ComposeEmailFormProps,
  ComposeEmailParentLink,
} from '@modules/_shared/hooks/composeEmailDraft';

const GET_COMPOSE_EMAIL_DRAFT = gql(/* GraphQL */ `
  query GetComposeEmailDraft(
    $userIds: [uuid!]!
    $emails: [citext!]!
    $fileIds: [uuid!]!
    $templateIds: [uuid!]!
    $replyToIds: [uuid!]!
    $leadIds: [uuid!]!
    $buyerLeadIds: [uuid!]!
    $enquiryIds: [uuid!]!
    $lotIds: [uuid!]!
    $contactIds: [uuid!]!
  ) {
    recipientsById: users(where: { id: { _in: $userIds } }) {
      id
      first_name
      last_name
      full_name
      emails(order_by: [{ primary: desc }, { verified: desc }]) {
        email
        primary
      }
    }
    recipientsByEmail: users(
      where: { emails: { email: { _in: $emails } } }
      order_by: { created_at: asc }
    ) {
      id
      first_name
      last_name
      full_name
      emails(order_by: [{ primary: desc }, { verified: desc }]) {
        email
        primary
      }
    }
    files(where: { id: { _in: $fileIds } }) {
      id
      name
      url
      size
      mimetype
    }
    email_templates(where: { id: { _in: $templateIds } }) {
      id
      ...EmailTemplateSelect
    }
    email_messages_aggregate(where: { id: { _in: $replyToIds } }) {
      nodes {
        id
        message_id
        created_by
        subject
        from
        cc
        bcc
        date
      }
    }
    leads(where: { id: { _in: $leadIds } }) {
      id
      first_name
      last_name
      contact {
        id
        first_name
        last_name
        full_name
      }
    }
    buyer_leads(where: { id: { _in: $buyerLeadIds } }) {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
      mls_user {
        id
      }
      lot {
        id
        title
      }
    }
    enquiries(where: { id: { _in: $enquiryIds } }) {
      id
      first_name
      last_name
      user {
        id
        first_name
        last_name
        full_name
      }
      lot {
        id
        title
      }
    }
    lots(where: { id: { _in: $lotIds } }) {
      id
      title
    }
    contacts: users(where: { id: { _in: $contactIds } }) {
      id
      first_name
      last_name
      full_name
    }
  }
`);

const GET_COMPOSE_EMAIL_TEMPLATE_SUGGESTIONS = gql(/* GraphQL */ `
  query GetComposeEmailTemplateSuggestions(
    $senderId: uuid
    $recipients: [uuid!]
    $leadId: uuid
    $listingId: uuid
    $buyerLeadId: uuid
    $enquiryId: uuid
    $language: GetEmailTemplateSuggestionsLanguage
  ) {
    get_email_template_suggestions(
      senderId: $senderId
      recipients: $recipients
      leadId: $leadId
      listingId: $listingId
      buyerLeadId: $buyerLeadId
      enquiryId: $enquiryId
      language: $language
    ) {
      suggestions
    }
  }
`);

export type ComposeEmailDraft =
  | { status: 'loading' }
  | {
      status: 'ready';
      input: ComposeEmailInput;
      delivery: ComposeEmailDelivery;
      formProps: ComposeEmailFormProps;
      parentLinks: ComposeEmailParentLink[];
      /** Plain text of the drafted body (sanitised), null for template/reply drafts where the composer changes the body. */
      draftBodyPlaintext: string | null;
      draftSubject: string;
    }
  | { status: 'error'; error: string };

const stringifyInput = (rawInput: unknown): string | null => {
  try {
    return JSON.stringify(rawInput) ?? null;
  } catch {
    return null;
  }
};

const errorText = (error: { message: string }): string =>
  error.message === '' ? JSON.stringify(error, null, 2) : error.message;

export const useComposeEmailDraft = (rawInput: unknown): ComposeEmailDraft => {
  const { t, language } = useLocale();
  const { me } = useAppData();

  // Tool inputs are re-created on every chat render; key on their content.
  const inputKey = stringifyInput(rawInput);
  const parsed = useMemo(
    () =>
      parseComposeEmailInput(inputKey == null ? null : JSON.parse(inputKey)),
    [inputKey],
  );
  const input = parsed.ok ? parsed.input : null;

  const { data, error } = useQuery(GET_COMPOSE_EMAIL_DRAFT, {
    variables: composeEmailDraftVariables(input),
    skip: input == null,
  });

  const typeLabels = useMemo<ComposeEmailTypeLabels>(
    () => ({
      lead: t('Lead'),
      buyer_lead: t('Buyer lead'),
      enquiry: t('Enquiry'),
      lot: t('Listing'),
      contact: t('Contact'),
    }),
    [t],
  );

  const resolved = useMemo(
    () =>
      input == null || data == null
        ? null
        : resolveComposeEmailDraft(input, data, typeLabels),
    [input, data, typeLabels],
  );

  const sanitizedBody = useMemo(
    () =>
      input?.bodyHtml == null ? null : sanitizeAgentEmailHtml(input.bodyHtml),
    [input],
  );

  const resolvedRecipients =
    resolved != null && resolved.ok ? resolved.toRecipients : null;
  const needsSuggestions =
    resolvedRecipients != null &&
    (input?.templateId != null ||
      (sanitizedBody != null && hasAgentEmailVariables(sanitizedBody)));
  const { data: suggestionsData, error: suggestionsError } = useQuery(
    GET_COMPOSE_EMAIL_TEMPLATE_SUGGESTIONS,
    {
      variables: {
        senderId: me?.id ?? null,
        recipients: (resolvedRecipients ?? []).map(recipient => recipient.id),
        ...(input == null ? {} : composeEmailSuggestionAnchors(input)),
        language: toSuggestionLanguage(language),
      },
      skip: !needsSuggestions,
    },
  );

  const templateSuggestions = useMemo(
    () =>
      suggestionsData == null
        ? null
        : parseSuggestionsMap(
            suggestionsData.get_email_template_suggestions?.suggestions,
          ),
    [suggestionsData],
  );

  const campaignMode = input?.delivery === 'individual';
  const body = useMemo(() => {
    if (sanitizedBody == null) {
      return null;
    }
    const contextSuggestions = templateSuggestions ?? [];
    const value = createValueFromString(
      linkAgentEmailVariables(
        sanitizedBody,
        campaignMode
          ? addCampaignRecipientSuggestions(contextSuggestions)
          : contextSuggestions,
      ),
      'html',
    );
    return { value, plaintext: value.toString('plaintext') };
  }, [sanitizedBody, templateSuggestions, campaignMode]);

  return useMemo((): ComposeEmailDraft => {
    if (!parsed.ok) {
      return { status: 'error', error: parsed.error };
    }
    if (error != null) {
      return { status: 'error', error: errorText(error) };
    }
    if (resolved == null) {
      return { status: 'loading' };
    }
    if (!resolved.ok) {
      return { status: 'error', error: resolved.error };
    }
    // Only a template needs them; an agent-written body is then shown with
    // its variables as plain text.
    if (
      needsSuggestions &&
      suggestionsError != null &&
      parsed.input.templateId != null
    ) {
      return { status: 'error', error: errorText(suggestionsError) };
    }
    if (
      needsSuggestions &&
      suggestionsError == null &&
      templateSuggestions == null
    ) {
      return { status: 'loading' };
    }

    const draftInput = parsed.input;
    const isReply = resolved.replyToMessage != null;
    return {
      status: 'ready',
      input: draftInput,
      delivery: resolved.delivery,
      formProps: composeEmailFormProps({
        input: draftInput,
        resolved,
        bodyValue: body?.value ?? null,
        // EmailForm's own suggestions have no lead/listing anchor: resolving
        // the agent's variables with them at send would blank them.
        templateSuggestions,
      }),
      parentLinks: resolved.parentLinks,
      draftBodyPlaintext: isReply ? null : body?.plaintext ?? null,
      draftSubject: composeEmailDraftSubject(draftInput, resolved),
    };
  }, [
    parsed,
    error,
    resolved,
    needsSuggestions,
    suggestionsError,
    templateSuggestions,
    body,
  ]);
};
